'use client'

import { useState, useEffect } from 'react'
import { pipefyApi } from '@/lib/api-client'
import { DataTable } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { Loader2, RefreshCw, ExternalLink } from 'lucide-react'

interface Pipe {
  id: string
  name: string
  internal_id: string
  description: string | null
  url: string
}

interface Webhook {
  id: string
  url: string
  actions: string[]
  headers: Record<string, string> | null
  active: boolean
  created_at: string
  updated_at: string
}

export default function WebhooksPage() {
  const [pipes, setPipes] = useState<Pipe[]>([])
  const [selectedPipeId, setSelectedPipeId] = useState<string>('')
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingWebhooks, setLoadingWebhooks] = useState(false)
  const [pipeInfo, setPipeInfo] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    fetchPipes()
  }, [])

  useEffect(() => {
    if (selectedPipeId) {
      fetchWebhooks(selectedPipeId)
    } else {
      setWebhooks([])
      setPipeInfo(null)
    }
  }, [selectedPipeId])

  const fetchPipes = async () => {
    setLoading(true)
    try {
      const result = await pipefyApi.listPipes({})
      if (result.success && result.pipes) {
        setPipes(result.pipes)
      } else {
        toast.error('Erro ao buscar pipes')
      }
    } catch (error: any) {
      console.error('Erro ao buscar pipes:', error)
      toast.error('Erro ao buscar pipes: ' + (error.message || 'Erro desconhecido'))
    } finally {
      setLoading(false)
    }
  }

  const fetchWebhooks = async (pipeId: string) => {
    setLoadingWebhooks(true)
    try {
      const result = await pipefyApi.getPipeWebhooks({ pipeId })
      if (result.success) {
        setWebhooks(result.webhooks || [])
        setPipeInfo(result.pipe || null)
      } else {
        toast.error(result.error || 'Erro ao buscar webhooks')
        setWebhooks([])
        setPipeInfo(null)
      }
    } catch (error: any) {
      console.error('Erro ao buscar webhooks:', error)
      toast.error('Erro ao buscar webhooks: ' + (error.message || 'Erro desconhecido'))
      setWebhooks([])
      setPipeInfo(null)
    } finally {
      setLoadingWebhooks(false)
    }
  }

  const handleRefresh = () => {
    if (selectedPipeId) {
      fetchWebhooks(selectedPipeId)
    } else {
      fetchPipes()
    }
  }

  const columns = [
    {
      key: 'url',
      label: 'URL',
      render: (webhook: Webhook) => (
        <div className="flex items-center gap-2">
          <a
            href={webhook.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
          >
            {webhook.url}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (webhook: Webhook) => (
        <div className="flex flex-wrap gap-1">
          {webhook.actions && webhook.actions.length > 0 ? (
            webhook.actions.map((action, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {action}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground text-sm">Nenhuma ação</span>
          )}
        </div>
      ),
    },
    {
      key: 'active',
      label: 'Status',
      render: (webhook: Webhook) => (
        <Badge variant={webhook.active ? 'default' : 'secondary'}>
          {webhook.active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'headers',
      label: 'Headers',
      render: (webhook: Webhook) => (
        <div className="text-sm">
          {webhook.headers && Object.keys(webhook.headers).length > 0 ? (
            <div className="flex flex-col gap-1">
              {Object.entries(webhook.headers).map(([key, value]) => (
                <div key={key} className="text-xs font-mono">
                  <span className="text-muted-foreground">{key}:</span> {value}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">Nenhum header</span>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (webhook: Webhook) => (
        <span className="text-sm">
          {webhook.created_at
            ? new Date(webhook.created_at).toLocaleString('pt-BR')
            : '-'}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Atualizado em',
      render: (webhook: Webhook) => (
        <span className="text-sm">
          {webhook.updated_at
            ? new Date(webhook.updated_at).toLocaleString('pt-BR')
            : '-'}
        </span>
      ),
    },
  ]

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Webhooks do Pipefy</h1>
          <p className="text-muted-foreground mt-2">
            Consulte os webhooks configurados para um pipe específico
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="bg-card rounded-lg border p-6 space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Selecione um Pipe</label>
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Carregando pipes...</span>
              </div>
            ) : (
              <Select value={selectedPipeId} onValueChange={setSelectedPipeId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione um pipe para ver os webhooks" />
                </SelectTrigger>
                <SelectContent>
                  {pipes.length === 0 ? (
                    <SelectItem value="no-pipes" disabled>
                      Nenhum pipe encontrado
                    </SelectItem>
                  ) : (
                    pipes.map((pipe) => (
                      <SelectItem key={pipe.id} value={pipe.id}>
                        {pipe.name} ({pipe.internal_id})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {pipeInfo && (
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-semibold mb-1">Pipe selecionado:</h3>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">{pipeInfo.name}</span> (ID: {pipeInfo.id})
            </p>
          </div>
        )}
      </div>

      {selectedPipeId && (
        <div className="bg-card rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Webhooks</h2>
            {loadingWebhooks && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Carregando...</span>
              </div>
            )}
          </div>

          {!loadingWebhooks && webhooks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum webhook encontrado para este pipe.</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={webhooks}
              loading={loadingWebhooks}
              emptyText="Nenhum webhook encontrado"
              searchable={true}
              searchPlaceholder="Buscar por URL, ações..."
            />
          )}
        </div>
      )}

      {!selectedPipeId && (
        <div className="bg-card rounded-lg border p-6">
          <div className="text-center py-8 text-muted-foreground">
            <p>Selecione um pipe acima para visualizar os webhooks configurados.</p>
          </div>
        </div>
      )}
    </div>
  )
}

